import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeEnumFetchFunction,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import UserCustomerAudit from 'Models/Security/Acl/UserCustomerAudit';
import UserMonitoredEntityAudit from 'Models/Security/Acl/UserMonitoredEntityAudit';
import VisitorsMonitoredEntityAudit from 'Models/Security/Acl/VisitorsMonitoredEntityAudit';
import * as Enums from '../Enums';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';
import { auditCategoryOptions } from 'Models/Enums/MappedEnumOptions';

export interface IMonitoredentityauditEntityAttributes extends IModelAttributes {
	title: string;
	description: string | null;
	category: Enums.auditCategory;
	eventId: string | null;

	monitorId: string;
	monitor: Models.MonitoredEntity | Models.IMonitoredEntityAttributes;
}

@entity('MonitoredentityauditEntity', 'MonitoredEntityAudit')
export default class MonitoredentityauditEntity extends Model
	implements IMonitoredentityauditEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new UserCustomerAudit(),
		new UserMonitoredEntityAudit(),
		new VisitorsMonitoredEntityAudit(),
	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];


	@Validators.Required()
	@observable
	@attribute<MonitoredentityauditEntity, string>()
	@CRUD({
		name: 'Title',
		displayType: 'textfield',
		order: 5,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public title: string;

	@Validators.Required()
	@observable
	@attribute<MonitoredentityauditEntity, string>()
	@CRUD({
		name: 'Description',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public description: string | null;
	
	@Validators.Required()
	@observable
	@attribute<MonitoredentityauditEntity, Enums.auditCategory>()
	@CRUD({
		name: 'Category',
		displayType: 'enum-combobox',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: (attr: string) => {
			return AttrUtils.standardiseEnum(attr, auditCategoryOptions);
		},
		enumResolveFunction: makeEnumFetchFunction(auditCategoryOptions),
		displayFunction: (attr: Enums.auditCategory) => auditCategoryOptions[attr],
	})
	public category: Enums.auditCategory;

	@attribute<MonitoredentityauditEntity, string>()
	@CRUD({
		name: 'Event Id',
		displayType: 'textfield',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseUuid,
	})
	public eventId: string | null;

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		
		name: 'Monitor',
		displayType: 'reference-combobox',
		order: 40,
		referenceTypeFunc: () => Models.MonitoredEntity,
		
	})
	public monitorId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public monitor: Models.MonitoredEntity;

	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IMonitoredentityauditEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IMonitoredentityauditEntityAttributes>) {
		
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.title !== undefined) {
				this.title = attributes.title;
			}
			if (attributes.description !== undefined) {
				this.description = attributes.description;
			}
			if (attributes.category !== undefined) {
				this.category = attributes.category;
			}
			if (attributes.eventId !== undefined) {
				this.eventId = attributes.eventId;
			}
			if (attributes.monitorId !== undefined) {
				this.monitorId = attributes.monitorId;
			}
			if (attributes.monitor !== undefined) {
				if (attributes.monitor === null) {
					this.monitor = attributes.monitor;
				} else if (attributes.monitor instanceof Models.MonitoredEntity) {
					this.monitor = attributes.monitor;
					this.monitorId = attributes.monitor.id;
				} else {
					this.monitor = new Models.MonitoredEntity(attributes.monitor);
					this.monitorId = this.monitor.id;
				}
			}
		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	
	public defaultExpands = `
		monitor {
			${Models.MonitoredEntity.getAttributes().join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
			},
		);
	}
	
	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(MonitoredentityauditEntity.prototype, 'created');
CRUD(modifiedAttr)(MonitoredentityauditEntity.prototype, 'modified');